import {
  BarChart, Users, Eye, MousePointer2, Heart, TrendingUp, Calendar,
  ChevronRight, ArrowUpRight, ArrowDownRight, Filter, Download
} from 'lucide-react';
import { cn } from '@/lib/utils';

const STATS = [
  { label: 'Tổng lượt xem', value: '1,284', change: '+12.5%', isUp: true, icon: <Eye size={22} />, color: 'text-blue-500', bg: 'bg-blue-50' },
  { label: 'Lượt nhắn tin', value: '45', change: '+5.2%', isUp: true, icon: <MousePointer2 size={22} />, color: 'text-purple-500', bg: 'bg-purple-50' },
  { label: 'Lượt lưu tin', value: '128', change: '-2.4%', isUp: false, icon: <Heart size={22} />, color: 'text-red-500', bg: 'bg-red-50' },
  { label: 'Tỉ lệ phản hồi', value: '85%', change: '+1.0%', isUp: true, icon: <Users size={22} />, color: 'text-green-500', bg: 'bg-green-50' },
];

const CHART_DATA = [
  { day: 'Thứ 2', views: 120 },
  { day: 'Thứ 3', views: 150 },
  { day: 'Thứ 4', views: 80 },
  { day: 'Thứ 5', views: 200 },
  { day: 'Thứ 6', views: 170 },
  { day: 'Thứ 7', views: 250 },
  { day: 'Chủ Nhật', views: 190 },
];

export default function UserAnalyticsPage() {
  const maxViews = Math.max(...CHART_DATA.map((d) => d.views));

  return (
    <div className="min-h-screen bg-[#f8f9fa] py-8 animate-fade-in">
      <div className="max-w-[1200px] mx-auto px-4">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Thống kê hiệu quả</h1>
            <p className="text-gray-500">Báo cáo chi tiết về hiệu suất tin đăng của bạn</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 shadow-sm cursor-pointer hover:bg-gray-50 transition-colors">
              <Calendar size={16} />
              <span>7 ngày qua</span>
            </div>
            <button className="p-2.5 bg-white border border-gray-200 rounded-xl text-gray-600 hover:text-gray-900 transition-colors shadow-sm">
              <Download size={18} />
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {STATS.map((stat) => (
            <div key={stat.label} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all">
              <div className="flex justify-between items-start mb-4">
                <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center', stat.bg, stat.color)}>{stat.icon}</div>
                <div className={cn(
                  'flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[11px] font-bold',
                  stat.isUp ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
                )}>
                  {stat.isUp ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                  {stat.change}
                </div>
              </div>
              <p className="text-sm font-medium text-gray-400 mb-1">{stat.label}</p>
              <h3 className="text-2xl font-bold text-gray-900">{stat.value}</h3>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Chart Section */}
          <div className="lg:col-span-2 bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Biểu đồ lượt xem tin</h3>
                <p className="text-sm text-gray-400">Dữ liệu được cập nhật đến 17:00 hôm nay</p>
              </div>
              <div className="flex items-center gap-4 text-xs font-bold uppercase tracking-wider">
                <div className="flex items-center gap-2 text-blue-500">
                  <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                  <span>Tuần này</span>
                </div>
              </div>
            </div>

            <div className="h-72 flex items-end gap-4 sm:gap-8 px-2 relative">
              {/* Grid Lines */}
              <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-full border-t border-gray-50 border-dashed" />
                ))}
              </div>

              {CHART_DATA.map((data) => (
                <div key={data.day} className="flex-1 flex flex-col items-center gap-4 group relative z-10">
                  <div className="relative w-full flex flex-col items-center justify-end h-full">
                    <div
                      className="w-full max-w-[42px] bg-blue-500/10 group-hover:bg-blue-500/20 rounded-t-xl transition-all duration-500 ease-out relative overflow-hidden"
                      style={{ height: `${(data.views / maxViews) * 100}%` }}
                    >
                      <div
                        className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-600 to-blue-400 transition-all duration-700 delay-100"
                        style={{ height: '100%' }}
                      />
                      {/* Interactive Tooltip */}
                      <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[11px] font-bold px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all shadow-xl whitespace-nowrap pointer-events-none scale-90 group-hover:scale-100">
                        {data.views} lượt xem
                      </div>
                    </div>
                  </div>
                  <span className="text-[11px] font-bold text-gray-400 uppercase tracking-tighter">{data.day}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Performance Listing */}
          <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm flex flex-col">
            <h3 className="font-bold text-gray-900 text-lg mb-8">Tin đăng nổi bật</h3>
            <div className="flex-1 space-y-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4 group cursor-pointer">
                  <div className="w-14 h-14 rounded-xl bg-gray-100 shrink-0 overflow-hidden shadow-sm group-hover:shadow-md transition-all">
                    <img src={`https://picsum.photos/seed/${i + 50}/200/200`} alt="Listing" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-bold text-gray-800 truncate group-hover:text-blue-600 transition-colors">Căn hộ F&B trung tâm Quận 3 cần cho thuê</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <div className="flex items-center gap-1 text-[11px] text-gray-400 font-bold">
                        <Eye size={12} className="text-blue-400" /> 428
                      </div>
                      <div className="flex items-center gap-1 text-[11px] text-gray-400 font-bold border-l pl-3">
                        <MousePointer2 size={12} className="text-purple-400" /> 12
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-gray-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                </div>
              ))}
            </div>
            <button className="w-full mt-10 py-3 text-[13px] font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-all active:scale-[0.98]">
              Xem chi tiết tất cả báo cáo
            </button>
          </div>
        </div>


      </div>
    </div>
  );
}
